import { useCallback, useEffect, useRef } from 'react';

const normalizePointer = (event) => ({
  clientX: event.clientX,
  clientY: event.clientY,
  screenX: Number.isFinite(event.clientX) ? event.clientX : event.screenX,
  screenY: Number.isFinite(event.clientY) ? event.clientY : event.screenY,
  pointerId: event.pointerId,
  pointerType: event.pointerType,
  target: event.target,
  currentTarget: event.currentTarget,
});

export const useTimelinePointerInteraction = ({ onStart, onFrame, onCommit, onCancel }) => {
  const callbacksRef = useRef({ onStart, onFrame, onCommit, onCancel });
  const activeRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    callbacksRef.current = { onStart, onFrame, onCommit, onCancel };
  }, [onStart, onFrame, onCommit, onCancel]);

  const cancelActive = useCallback((reason = 'cancelled') => {
    if (!activeRef.current) return;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    const active = activeRef.current;
    activeRef.current = null;
    callbacksRef.current.onCancel?.(active.point, active.kind, active.data, reason);
  }, []);

  const handlePointerDown = useCallback((event, kind) => {
    if (event.button !== 0) return;
    if (activeRef.current) {
      if (event.pointerType === 'touch') cancelActive('multitouch');
      return;
    }

    const point = normalizePointer(event);
    const data = callbacksRef.current.onStart?.(point, kind, event);
    if (data === false) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    activeRef.current = { pointerId: event.pointerId, point, kind, data, target: event.currentTarget };
  }, [cancelActive]);

  const handlePointerMove = useCallback((event) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault();
    active.point = normalizePointer(event);
    if (frameRef.current) return;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const latest = activeRef.current;
      if (!latest) return;
      latest.framePromise = Promise.resolve(
        callbacksRef.current.onFrame?.(latest.point, latest.kind, latest.data),
      );
    });
  }, []);

  const handlePointerUp = useCallback(async (event) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    active.point = normalizePointer(event);
    activeRef.current = null;
    active.target?.releasePointerCapture?.(event.pointerId);
    await active.framePromise;
    await callbacksRef.current.onFrame?.(active.point, active.kind, active.data, true);
    await callbacksRef.current.onCommit?.(active.point, active.kind, active.data);
  }, []);

  const handlePointerCancel = useCallback((event) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    active.target?.releasePointerCapture?.(event.pointerId);
    cancelActive('pointercancel');
  }, [cancelActive]);

  const bindPointerInteraction = useCallback((kind) => ({
    onPointerDown: (event) => handlePointerDown(event, kind),
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  }), [handlePointerCancel, handlePointerDown, handlePointerMove, handlePointerUp]);

  useEffect(() => () => cancelActive('unmount'), [cancelActive]);

  return bindPointerInteraction;
};
